// src/components/facility/panels/WorkforcePanel.js
import React, { useState } from 'react';
import { ProgressBar } from '../../ui/ProgressBar';

export function WorkforcePanel({ state, dispatch }) {
  const [pendingHire, setPendingHire] = useState(null);
  const [assignRoomId, setAssignRoomId] = useState('');

  function handleHireConfirm() {
    if (!pendingHire) return;
    dispatch({ type: 'HIRE_WORKER', payload: { workerData: pendingHire } });
    if (assignRoomId) {
      setTimeout(() => {
        dispatch({ type: 'ASSIGN_WORKER', payload: { workerId: pendingHire.id, roomId: assignRoomId } });
      }, 50);
    }
    setPendingHire(null);
    setAssignRoomId('');
  }

  const weeklyWages = state.workers.reduce((s, w) => s + w.currentWage, 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

      {/* Payroll summary */}
      <div style={{ display: 'flex', gap: 10 }}>
        <div style={{ flex: 1, background: '#1a1810', border: '1px solid #2a2618', borderRadius: 6, padding: '8px 12px', textAlign: 'center' }}>
          <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>Staff</div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>{state.workers.length}</div>
        </div>
        <div style={{ flex: 1, background: '#1a1810', border: '1px solid #2a2618', borderRadius: 6, padding: '8px 12px', textAlign: 'center' }}>
          <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>Weekly Wages</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--yellow)' }}>${weeklyWages.toLocaleString()}</div>
        </div>
      </div>

      {/* Staff list */}
      <div>
        <div style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          Current Staff
        </div>
        {state.workers.length === 0 ? (
          <div style={{ fontSize: 12, color: 'var(--text-dim)', textAlign: 'center', padding: '12px 0' }}>
            No staff hired yet.
          </div>
        ) : (
          state.workers.map(worker => {
            const isIdle = !worker.assignedRoomId;
            return (
              <div key={worker.id} style={{
                background: '#1a1810',
                border: `1px solid ${isIdle ? 'var(--yellow-dim)' : '#2a2618'}`,
                borderRadius: 6, padding: '8px 10px', marginBottom: 6,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <div>
                    <span style={{ fontWeight: 600, fontSize: 13 }}>{worker.name}</span>
                    <span className={`type-badge ${worker.type}`} style={{ marginLeft: 8 }}>{worker.type}</span>
                    {isIdle && <span style={{ fontSize: 10, color: 'var(--yellow)', marginLeft: 6 }}>● Idle</span>}
                  </div>
                  <span style={{ fontSize: 11, color: 'var(--yellow)' }}>${worker.currentWage}/wk</span>
                </div>
                <div style={{ display: 'flex', gap: 10, marginBottom: 6 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 10, color: 'var(--text-dim)', marginBottom: 2 }}>Skill {worker.skill}</div>
                    <ProgressBar value={worker.skill} size="sm" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 10, color: 'var(--text-dim)', marginBottom: 2 }}>Morale {worker.morale}</div>
                    <ProgressBar value={worker.morale} size="sm" />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <select
                    value={worker.assignedRoomId || ''}
                    onChange={e => {
                      const roomId = e.target.value;
                      if (roomId) dispatch({ type: 'ASSIGN_WORKER', payload: { workerId: worker.id, roomId } });
                      else dispatch({ type: 'UNASSIGN_WORKER', payload: { workerId: worker.id } });
                    }}
                    style={{ flex: 1, background: 'var(--border-light)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text)', padding: '3px 6px', fontSize: 11 }}
                  >
                    <option value="">— Unassigned —</option>
                    {worker.type === 'scientist' && <option value="lab">🔬 Research Lab</option>}
                    {state.rooms.filter(r => r.unlocked).map(r => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                  </select>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => dispatch({ type: 'FIRE_WORKER', payload: { workerId: worker.id } })}
                  >Fire</button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Job market */}
      {(state.jobMarket || []).length > 0 && (
        <div>
          <div style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Job Market
          </div>
          {state.jobMarket.map(candidate => {
            const fee = Math.round(candidate.wageDemand * 0.5);
            return (
              <div key={candidate.id} style={{ background: '#181610', border: '1px solid #2a2618', borderRadius: 6, padding: '8px 10px', marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <span style={{ fontWeight: 600, fontSize: 13 }}>{candidate.name}</span>
                  <span className={`type-badge ${candidate.type}`} style={{ marginLeft: 8 }}>{candidate.type}</span>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                    Skill {candidate.skill} · ${candidate.wageDemand}/wk
                  </div>
                </div>
                <button
                  className="btn btn-primary btn-sm"
                  disabled={state.cash < fee}
                  onClick={() => { setPendingHire(candidate); setAssignRoomId(''); }}
                >
                  Hire (${fee})
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Hire confirm modal */}
      {pendingHire && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
          zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div className="panel" style={{ width: 320, padding: '22px 18px' }}>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>Hire {pendingHire.name}?</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>
              <span className={`type-badge ${pendingHire.type}`}>{pendingHire.type}</span>
              <span style={{ marginLeft: 8 }}>Skill {pendingHire.skill} · ${pendingHire.wageDemand}/wk</span>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>Assign to room (optional):</div>
            <select
              value={assignRoomId}
              onChange={e => setAssignRoomId(e.target.value)}
              style={{ width: '100%', background: 'var(--border-light)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text)', padding: '6px 8px', fontSize: 12, marginBottom: 16 }}
            >
              <option value="">— Unassigned —</option>
              {pendingHire.type === 'scientist' && <option value="room-lab">🔬 Research Lab</option>}
              {state.rooms.filter(r => r.unlocked && r.type !== 'lab').map(r => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary" style={{ flex: 1 }} onClick={handleHireConfirm}>
                ✓ Hire (${Math.round(pendingHire.wageDemand * 0.5)} fee)
              </button>
              <button className="btn btn-secondary" onClick={() => setPendingHire(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
